import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import List from 'apollo-react/components/List';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ControlProperty from './ControlProperty';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { updateControlProperty } from '../actions/report-designer.actions';
import {
  REPORT_CONTROL_TYPE,
  REPORT_MODEL_ATTRIBUTES,
} from '../constants/report-designer.constants';
import { PROPERTIES } from '../constants/report-designer.properties.constants';
import {
  getControlProperty,
  getIsControlPropChanged,
  getSelectedControlId,
} from '../selectors/report-designer.selectors';

const useStyles = makeStyles({
  root: {
    '& div[class*="MuiAccordionSummary-root"]': {
      marginLeft: '-2rem',
    },
  },
  controlTypeWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  controlType: {
    padding: 5,
    color: '#0768fd',
  },
  propertiesContainer: {
    padding: '0px 24px 24px 24px',
  },
  uiChanges: {
    '& textarea': {
      border: '1px solid orange !important',
    },
    '& textarea:hover': {
      border: '1px solid orange !important',
    },
  },
});

const ControlProperties = () => {
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();

  const selectedControlId = useSelector(getSelectedControlId);
  const controlType = useSelector((state) =>
    getControlProperty(state, { id: selectedControlId, prop: REPORT_MODEL_ATTRIBUTES.CONTROL_TYPE })
  );
  const descriptionValueStore = useSelector((state) =>
    getControlProperty(state, { id: REPORT_CONTROL_TYPE.DISTRIBUTION, prop: 'description' })
  );
  const isDescriptionValueChanged = useSelector((state) =>
    getIsControlPropChanged(state, { id: REPORT_CONTROL_TYPE.DISTRIBUTION, prop: 'description' })
  );

  const [description, setDescriptionValue] = useState(descriptionValueStore);

  const attributes = PROPERTIES[controlType] || [];
  const showDescriptionField = controlType === REPORT_CONTROL_TYPE.REPORT_INFO;

  const handleDescriptionChange = ({ target: { value } }) => {
    const newValue = value.trim();
    setDescriptionValue(newValue);

    dispatch(updateControlProperty(REPORT_CONTROL_TYPE.DISTRIBUTION, 'description', newValue));
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4">{translateString('Control Properties')}</Typography>
      <div className={classes.controlTypeWrapper}>
        {controlType && <h5 className={classes.controlType}>{controlType}</h5>}
      </div>
      <div>
        <div className={classes.propertiesContainer}>
          <List>
            {attributes.length ? (
              <>
                {attributes.map(({ label, properties }) => (
                  <div key={label}>
                    <Accordion defaultExpanded>
                      <AccordionSummary>
                        <Typography>{label}</Typography>
                      </AccordionSummary>
                      {properties.map((props) => (
                        <ControlProperty
                          key={props.label}
                          controlId={selectedControlId}
                          {...props}
                        />
                      ))}
                    </Accordion>
                  </div>
                ))}
                {showDescriptionField && (
                  <TextField
                    label={translateString('Description')}
                    value={description ?? ''}
                    sizeAdjustable
                    multiline
                    fullWidth
                    onBlur={handleDescriptionChange}
                    onChange={({ target: { value } }) => setDescriptionValue(value)}
                    className={isDescriptionValueChanged ? classes.uiChanges : null}
                    inputProps={{
                      rows: 2,
                      style: {
                        height: 'auto',
                        overflow: 'auto',
                        resize: 'none',
                      },
                      'aria-label': translateString('Description'),
                    }}
                  />
                )}
              </>
            ) : (
              <Typography>
                {controlType
                  ? `There are no property definitions for ${controlType}`
                  : 'Select a control'}
              </Typography>
            )}
          </List>
        </div>
      </div>
    </div>
  );
};

export default ControlProperties;
